import { listed, plural } from './format.js';
import { MAX_LISTED, age, bytes, cores, eventsAbout, groupBy, hasItems, hoursBetween, insideOf, kindOf, quantity, workloadOf, } from './inside.js';
import { isEvaluation } from './model.js';
import { THRESHOLDS } from './thresholds.js';
/**
 * Whether the cluster can place what it is asked to (IM-206): a NodePool's
 * ceiling, pods waiting for a place, and requests that do not say what a
 * workload uses — the three that, stacked, kept a production instance at ten
 * busy nodes while work queued behind them.
 */
const W = THRESHOLDS.workloads;
/** The warning events, or none when they could not be read — evidence, never the finding itself. */
const warningsIn = (events) => hasItems(events) ? events.items : [];
/**
 * NodePool limits against what the pool already holds (IM-206).
 *
 * Karpenter will not launch a node that takes a pool past its `limits`, and
 * says so only in its own log and a pod event. The symptom is pods Pending and
 * an autoscaler that looks idle, which reads as a scheduler problem. A memory
 * limit of 80Gi on a pool of t4g.large nodes is exactly ten nodes, and one pool
 * had sat at ten since it was created.
 *
 * Room is counted in nodes of the smallest size the pool already runs: the
 * pool may be allowed smaller types, but a node it is running is one it will
 * launch again. Karpenter's own "exceed limits" event decides it outright.
 */
export const nodePoolLimitsCheck = {
    id: 'nodepool-limits',
    title: 'NodePools have room to add nodes',
    category: 'capacity',
    outcomes: ['ok', 'warn', 'fail', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const inside = insideOf(facts);
        if (isEvaluation(inside))
            return inside;
        const pools = kindOf(inside.value, 'nodePools', 'the NodePools');
        if (!hasItems(pools))
            return pools;
        if (pools.items.length === 0) {
            return {
                outcome: 'not-applicable',
                finding: 'This cluster has no NodePools, so Karpenter does not add its nodes.',
                evidence: [],
            };
        }
        const nodesRead = kindOf(inside.value, 'nodes', 'the nodes');
        const nodes = hasItems(nodesRead) ? nodesRead.items : [];
        const warnings = warningsIn(kindOf(inside.value, 'events', 'the events'));
        const judged = pools.items.map((pool) => {
            const capped = warnings.filter((e) => /exceed limits for nodepool/i.test(e.message ?? '') &&
                (e.message ?? '').includes(pool.name));
            const limited = ['cpu', 'memory'].flatMap((resource) => {
                const limit = quantity(pool.limits[resource]);
                return limit === undefined
                    ? []
                    : [{ resource, limit, used: quantity(pool.usage[resource]) ?? 0 }];
            });
            const own = nodes.filter((n) => n.pool?.kind === 'nodepool' && n.pool.name === pool.name);
            const smallest = (resource) => {
                const sizes = own.flatMap((n) => {
                    const q = quantity(n.capacity[resource]);
                    return q === undefined ? [] : [q];
                });
                return sizes.length === 0 ? undefined : Math.min(...sizes);
            };
            const fits = limited.flatMap(({ resource, limit, used }) => {
                const size = smallest(resource);
                return size === undefined || size <= 0 ? [] : [Math.floor((limit - used) / size)];
            });
            const room = fits.length === 0 ? undefined : Math.max(0, Math.min(...fits));
            const full = limited.some(({ limit, used }) => used >= limit);
            const typeOf = own.find((n) => n.instanceType !== undefined)?.instanceType;
            const shown = (r) => r.resource === 'memory'
                ? `memory ${bytes(r.used)} of ${bytes(r.limit)}`
                : `CPU ${cores(r.used)} of ${cores(r.limit)}`;
            const value = limited.length === 0
                ? 'no limit set'
                : [
                    listed(limited.map(shown)),
                    room === undefined
                        ? undefined
                        : `room for ${plural(room, 'more node')}${typeOf === undefined ? '' : ` of ${typeOf}`}`,
                    capped.length === 0
                        ? undefined
                        : `Karpenter refused to launch ${plural(capped.reduce((n, e) => n + (e.count ?? 1), 0), 'time')}: instance types exceed the pool’s limits`,
                ]
                    .filter((part) => part !== undefined)
                    .join(' — ');
            const outcome = capped.length > 0 || full || room === 0
                ? 'fail'
                : room !== undefined && room < W.nodePoolHeadroomWarnNodes
                    ? 'warn'
                    : 'ok';
            return { pool: pool.name, outcome, evidence: { label: `NodePool ${pool.name}`, value } };
        });
        const failing = judged.filter((j) => j.outcome === 'fail').map((j) => j.pool);
        const tight = judged.filter((j) => j.outcome === 'warn').map((j) => j.pool);
        const evidence = judged.map((j) => j.evidence);
        if (failing.length > 0) {
            return {
                outcome: 'fail',
                finding: `${listed(failing.map((p) => `NodePool ${p}`))} ${failing.length === 1 ? 'is' : 'are'} at ${failing.length === 1 ? 'its' : 'their'} limit and cannot add a node.`,
                recommendation: 'Raise the limit to the most nodes the pool should ever run. Pods that need a new node stay Pending until then, while the autoscaler appears idle.',
                evidence,
            };
        }
        if (tight.length > 0) {
            return {
                outcome: 'warn',
                finding: `${listed(tight.map((p) => `NodePool ${p}`))} can add only one more node before ${tight.length === 1 ? 'its' : 'their'} limit.`,
                recommendation: 'Raise the limit before the next busy period needs more than one node.',
                evidence,
            };
        }
        return {
            outcome: 'ok',
            finding: `Every NodePool has room for at least ${plural(W.nodePoolHeadroomWarnNodes, 'more node')}.`,
            evidence,
        };
    },
};
/** Why a Pending pod is waiting, in the cluster's own words. */
const waitingOn = (pod) => {
    const scheduled = pod.conditions.find((c) => c.type === 'PodScheduled' && c.status === 'False');
    if (scheduled !== undefined)
        return scheduled.message ?? scheduled.reason;
    const waiting = [...pod.initContainers, ...pod.containers].find((c) => c.state?.state === 'waiting' && c.state.reason !== undefined);
    return waiting?.state?.reason;
};
/**
 * Pods Pending longer than a node takes to join (IM-206).
 *
 * The scheduler's own reason travels with each workload — "Insufficient cpu"
 * with the NodePool at its limit is the cap above; an image pull error is the
 * registry — and the count of `FailedScheduling` events in the cluster's last
 * hour beside it.
 */
export const pendingPodsCheck = {
    id: 'pending-pods',
    title: 'No pod waits long for a place to run',
    category: 'capacity',
    outcomes: ['ok', 'warn', 'fail', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const inside = insideOf(facts);
        if (isEvaluation(inside))
            return inside;
        const pods = kindOf(inside.value, 'pods', 'the pods');
        if (!hasItems(pods))
            return pods;
        const warnings = warningsIn(kindOf(inside.value, 'events', 'the events'));
        const waiting = pods.items.flatMap((pod) => {
            if (pod.phase !== 'Pending' || pod.createdAt === undefined)
                return [];
            const minutes = hoursBetween(pod.createdAt, facts.at) * 60;
            return minutes >= W.pendingWarnMinutes ? [{ pod, minutes }] : [];
        });
        if (waiting.length === 0) {
            return {
                outcome: 'ok',
                finding: `No pod has been Pending longer than ${String(W.pendingWarnMinutes)} minutes.`,
                evidence: [
                    {
                        label: 'Pending now',
                        value: plural(pods.items.filter((p) => p.phase === 'Pending').length, 'pod'),
                    },
                ],
            };
        }
        const groups = [...groupBy(waiting, (w) => workloadOf(w.pod)).entries()]
            .map(([workload, members]) => ({
            workload,
            members,
            oldest: Math.max(...members.map((m) => m.minutes)),
        }))
            .sort((a, b) => b.oldest - a.oldest);
        const oldest = groups[0]?.oldest ?? 0;
        const evidence = groups.slice(0, MAX_LISTED).map((g) => {
            const scheduling = eventsAbout(warnings, g.members.map((m) => m.pod))
                .filter((e) => e.reason === 'FailedScheduling')
                .reduce((n, e) => n + (e.count ?? 1), 0);
            const why = g.members.map((m) => waitingOn(m.pod)).find((r) => r !== undefined);
            return {
                label: g.workload,
                value: [
                    `${plural(g.members.length, 'pod')} Pending, the longest for ${age(g.oldest / 60)}`,
                    why,
                    scheduling === 0
                        ? undefined
                        : `${plural(scheduling, 'scheduling failure')} in the last hour`,
                ]
                    .filter((part) => part !== undefined)
                    .join(' — '),
            };
        });
        if (groups.length > MAX_LISTED) {
            evidence.push({ label: 'And', value: plural(groups.length - MAX_LISTED, 'more workload') });
        }
        return {
            outcome: oldest >= W.pendingFailMinutes ? 'fail' : 'warn',
            finding: `${plural(waiting.length, 'pod')} in ${plural(groups.length, 'workload')} ${waiting.length === 1 ? 'has' : 'have'} been Pending for up to ${age(oldest / 60)}.`,
            recommendation: 'Read each reason. "Insufficient cpu" or "memory" while no node is being added usually means a NodePool limit; a pull error means the image or the registry credentials.',
            evidence,
        };
    },
};
/**
 * CPU and memory requests against what the pods actually use (IM-206).
 *
 * The scheduler and Karpenter place pods by their requests, and never look at
 * usage. A worker requesting 100m and using a full core is invisible load:
 * the nodes run hot, nothing scales, and the pods fail their health checks.
 * The opposite — a request several times the use — is capacity paid for and
 * held idle. Usage is a snapshot from the metrics API, not a peak, which is
 * why the over-reserved ratio is wider than the under-reserved one.
 *
 * Job pods are left out: a sync job's whole life is a burst, and a snapshot
 * of one says nothing about what it should request. So is `kube-system`: the
 * cloud's own add-ons live there, the networking plugin among them, and their
 * requests are the cloud's to set — the first live run named `aws-node`.
 */
export const requestsUsageCheck = {
    id: 'requests-usage',
    title: 'Requests reflect what workloads use',
    category: 'capacity',
    outcomes: ['ok', 'warn', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const inside = insideOf(facts);
        if (isEvaluation(inside))
            return inside;
        const pods = kindOf(inside.value, 'pods', 'the pods');
        if (!hasItems(pods))
            return pods;
        const metrics = kindOf(inside.value, 'podMetrics', 'pod usage');
        if (!hasItems(metrics)) {
            // Not served is not "nothing to judge" here: every cluster has requests.
            return isEvaluation(metrics) && metrics.outcome === 'not-applicable'
                ? {
                    outcome: 'unknown',
                    finding: 'Could not compare requests with usage: the cluster reports no pod usage.',
                    recommendation: 'Install the metrics server, which the autoscalers need too.',
                    evidence: [{ label: 'Reason', value: metrics.finding }],
                }
                : metrics;
        }
        const usage = new Map(metrics.items.map((m) => [`${m.namespace}/${m.name}`, m]));
        const measured = pods.items.filter((p) => p.owner?.kind !== 'Job' &&
            p.namespace !== 'kube-system' &&
            usage.has(`${p.namespace}/${p.name}`));
        if (measured.length === 0) {
            return {
                outcome: 'not-applicable',
                finding: 'No running pod reports usage to compare its requests with.',
                evidence: [],
            };
        }
        const footprints = [...groupBy(measured, workloadOf).entries()].map(([workload, members]) => {
            let cpuRequest = 0;
            let memoryRequest = 0;
            let cpuUsed = 0;
            let memoryUsed = 0;
            for (const pod of members) {
                for (const c of pod.containers) {
                    cpuRequest += quantity(c.requests['cpu']) ?? 0;
                    memoryRequest += quantity(c.requests['memory']) ?? 0;
                }
                for (const c of usage.get(`${pod.namespace}/${pod.name}`)?.containers ?? []) {
                    cpuUsed += quantity(c.cpu) ?? 0;
                    memoryUsed += quantity(c.memory) ?? 0;
                }
            }
            return { workload, cpuRequest, cpuUsed, memoryRequest, memoryUsed };
        });
        const under = footprints.flatMap((f) => {
            const cpu = f.cpuUsed >= W.minCpuCores && f.cpuUsed >= W.underRequestedRatio * f.cpuRequest;
            const memory = f.memoryUsed >= W.minMemoryBytes && f.memoryUsed >= W.underRequestedRatio * f.memoryRequest;
            return cpu || memory ? [{ f, cpu, memory }] : [];
        });
        const over = footprints.flatMap((f) => {
            const cpu = f.cpuRequest >= W.overRequestedMinCpuCores &&
                f.cpuRequest >= W.overRequestedRatio * f.cpuUsed;
            const memory = f.memoryRequest >= W.overRequestedMinMemoryBytes &&
                f.memoryRequest >= W.overRequestedRatio * f.memoryUsed;
            return cpu || memory ? [{ f, cpu, memory }] : [];
        });
        /** The larger over the smaller, so the figure says how far apart they are. */
        const times = (larger, smaller) => `${String(Math.round((larger / smaller) * 10) / 10)}×`;
        const pair = (resource, u, r, show) => r === 0
            ? `${resource} ${show(u)} used, nothing requested`
            : u === 0
                ? `${resource} ${show(r)} requested, nothing used`
                : u >= r
                    ? `${resource} ${show(u)} used, ${show(r)} requested (${times(u, r)} the request)`
                    : `${resource} ${show(r)} requested, ${show(u)} used (${times(r, u)} the use)`;
        const line = (f, cpu, memory) => [
            cpu ? pair('CPU', f.cpuUsed, f.cpuRequest, cores) : undefined,
            memory ? pair('memory', f.memoryUsed, f.memoryRequest, bytes) : undefined,
        ]
            .filter((part) => part !== undefined)
            .join('; ');
        /**
         * Worst first, by the wider of the flagged gaps — memory as well as CPU,
         * or a workload flagged for memory alone sorts as though it were fine,
         * and the cap at `MAX_LISTED` drops it ahead of a milder one.
         */
        const worstFirst = (flagged, uses) => {
            // Nothing on the smaller side is the widest gap there is, not a division by zero.
            const gap = (used, requested) => uses
                ? used / Math.max(requested, Number.EPSILON)
                : requested / Math.max(used, Number.EPSILON);
            const width = ({ f, cpu, memory }) => Math.max(cpu ? gap(f.cpuUsed, f.cpuRequest) : 0, memory ? gap(f.memoryUsed, f.memoryRequest) : 0);
            return [...flagged].sort((a, b) => width(b) - width(a));
        };
        const evidence = [
            ...worstFirst(under, true)
                .slice(0, MAX_LISTED)
                .map(({ f, cpu, memory }) => ({
                label: `Uses more: ${f.workload}`,
                value: line(f, cpu, memory),
            })),
            ...worstFirst(over, false)
                .slice(0, MAX_LISTED)
                .map(({ f, cpu, memory }) => ({
                label: `Holds more: ${f.workload}`,
                value: line(f, cpu, memory),
            })),
            {
                label: 'Compared',
                value: `${plural(footprints.length, 'workload')}, by a snapshot of usage`,
            },
        ];
        if (under.length > 0) {
            return {
                outcome: 'warn',
                finding: `${plural(under.length, 'workload')} ${under.length === 1 ? 'uses' : 'use'} several times what ${under.length === 1 ? 'it requests' : 'they request'}, so the scheduler and the autoscaler cannot see that load.`,
                recommendation: 'Raise those requests towards what they use. Nodes then fill to what they can carry and the autoscaler adds one before the pods start failing their health checks.',
                evidence,
            };
        }
        if (over.length > 0) {
            return {
                outcome: 'warn',
                finding: `${plural(over.length, 'workload')} ${over.length === 1 ? 'requests' : 'request'} several times what ${over.length === 1 ? 'it uses' : 'they use'}, holding capacity that is paid for and idle.`,
                recommendation: 'Lower those requests once their peak use is known; a snapshot is not the peak.',
                evidence,
            };
        }
        return {
            outcome: 'ok',
            finding: `Requests are within range of use for all ${plural(footprints.length, 'workload')} compared.`,
            evidence,
        };
    },
};
//# sourceMappingURL=workloads.js.map